import React, { ReactNode } from "react";

import { AnalyticsAggregateType } from "@formant/core/model/AnalyticsAggregateType";
import { analyticsAggregateTypes } from "@formant/core/model/analyticsAggregateTypes";
import { IEventColumn } from "@formant/core/model/IEventColumn";
import { IStreamColumn } from "@formant/core/model/IStreamColumn";
import { ITableColumn } from "@formant/core/model/ITableColumn";
import { ITaskReportColumn } from "@formant/core/model/ITaskReportColumn";
import { Uuid } from "@formant/core/model/Uuid";

import { analyticsStore } from "../../stores";
import { ListForm } from "../common/ListForm";
import { SelectField } from "../common/SelectField";
import { Label } from "../JsonSchemaForm/Label";
import { Header } from "../Settings/EditAnalyticsModule/EditAnalytisModule.styles";
import { NewAnalyticsPicker } from "./NewAnalyticsPicker";

interface IGenericPickerProps {
    selectedTableColumns: ITableColumn[];
    deviceIds: string[];
    onSelection?: () => void;
    onReplaceTableColumns: (tableColumn: ITableColumn[]) => void;
}

export function GenericPicker(props: IGenericPickerProps) {
    const { selectedTableColumns, deviceIds, onReplaceTableColumns } = props;

    const [loading, setLoading] = React.useState<boolean>(false);

    const toStream = (tableColumn: ITableColumn): IStreamColumn => {
        return tableColumn as IStreamColumn;
    };

    const toTaskReport = (tableColumn: ITableColumn): ITaskReportColumn => {
        return tableColumn as ITaskReportColumn;
    };

    const toEvent = (tableColumn: ITableColumn): IEventColumn => {
        return tableColumn as IEventColumn;
    };

    const toId = (tableColumn: ITableColumn): string => {
        if (tableColumn.type === "stream") {
            return toStream(tableColumn).streamName;
        } else if (tableColumn.type === "task-report") {
            const task = toTaskReport(tableColumn);
            // if we ever want to support multiple task reports from different tables
            // add the table name to unique id.
            return task.columnName;
        } else if (tableColumn.type === "event") {
            return toEvent(tableColumn).eventType;
        }
        return "";
    };

    const equals = (
        tableColumnA: ITableColumn,
        tableColumnB: ITableColumn,
    ): boolean => {
        return toId(tableColumnA) === toId(tableColumnB);
    };

    const deleteColumn = (tableColumn: ITableColumn | undefined) => {
        if (tableColumn === undefined) {
            onReplaceTableColumns(selectedTableColumns);
            return;
        }
        onReplaceTableColumns(
            selectedTableColumns.filter(_ => !equals(_, tableColumn)),
        );
    };

    const onAddTableColumn = (tableColumn: ITableColumn, exists: boolean) => {
        if (exists) {
            deleteColumn(tableColumn);
        } else {
            onReplaceTableColumns([
                ...(selectedTableColumns || []),
                tableColumn,
            ]);
        }
    };

    const handleAdd = async () => {
        setLoading(true);
        await analyticsStore.realListFilteredStreams({
            deviceIds: deviceIds as Uuid[],
        });
        await analyticsStore.realGetListTaskReports({
            deviceIds: deviceIds as Uuid[],
        });
        setLoading(false);
    };

    const handleRenderDrawer = (id: string): ReactNode => {
        const selectedColumn = selectedTableColumns.find(_ => toId(_) === id);

        if (!selectedColumn) {
            return null;
        }

        const editAggregateLevel = (type: AnalyticsAggregateType) => {
            const updatedColumns = selectedTableColumns.map(column => {
                if (toId(column) === id) {
                    return { ...column, aggregateType: type };
                }
                return column;
            });
            onReplaceTableColumns(updatedColumns);
        };

        const editAggregateGroupBy = (groupBy: string[]) => {
            const updatedColumns = selectedTableColumns.map(column => {
                if (toId(column) === id) {
                    return { ...column, groupBy };
                }
                return column;
            });
            onReplaceTableColumns(updatedColumns);
        };
        return (
            <>
                <Label
                    value="Analytics"
                    link="https://docs.formant.io/docs/create-an-analytics-view-and-add-modules"
                    description="Choose the aggregation level of the value"
                />

                <SelectField
                    label="aggregate"
                    value={selectedColumn?.aggregateType || "count"}
                    onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
                        editAggregateLevel(
                            event.target.value as AnalyticsAggregateType,
                        )
                    }
                >
                    {analyticsAggregateTypes.map(_ => (
                        <option key={_} value={_}>
                            {_}
                        </option>
                    ))}
                </SelectField>

                {selectedColumn.type === "event" && (
                    <>
                        <Header type="list">Group by</Header>
                        <SelectField
                            label="group by"
                            value={
                                selectedColumn?.groupBy?.find(_ => _) ||
                                undefined
                            }
                            onChange={(
                                event: React.ChangeEvent<HTMLSelectElement>,
                            ) =>
                                editAggregateGroupBy(
                                    event.target.value !== "none"
                                        ? [event.target.value]
                                        : [],
                                )
                            }
                        >
                            <option value={undefined}>none</option>
                            <option value="type">type</option>
                            <option value="severity">severity</option>
                        </SelectField>
                    </>
                )}
            </>
        );
    };

    return (
        <ListForm
            label="Values"
            description="Data streams, events, or tasks to plot on the Y-axis. Once added, click a value again to apply aggregate functions."
            link="https://docs.formant.io/docs/create-an-analytics-view-and-add-modules"
            items={selectedTableColumns.map(_ => toId(_))}
            onDelete={(item: string) => {
                deleteColumn(selectedTableColumns.find(_ => toId(_) === item));
            }}
            onReorder={(items: string[]) => {
                onReplaceTableColumns(
                    items.flatMap(
                        item =>
                            selectedTableColumns.find(_ => item === toId(_)) ||
                            [],
                    ),
                );
            }}
            handleAdd={handleAdd}
            isLoadingOptions={loading}
            popup={
                <NewAnalyticsPicker
                    type={undefined}
                    selectedTableColumns={selectedTableColumns}
                    deviceIds={deviceIds}
                    onSelectTableColumn={(
                        tableColumn: ITableColumn,
                        exists: boolean,
                    ) => onAddTableColumn(tableColumn, exists)}
                />
            }
            drawer={handleRenderDrawer}
        />
    );
}
